<script setup>
import { Link } from '@inertiajs/vue3';
import GuestLayout from '@/Layouts/GuestLayout.vue';
import HeroSection from '@/Components/HeroSection.vue';
import WelcomeConnectedSection from '@/Components/WelcomeConnectedSection.vue';
import WelcomeBlackDiv from '@/Components/WelcomeBlackDiv.vue';

import GirlWithAss from '../../../public/images/heroes/GirlShowingAss.jpg';
</script>

<template>
    <GuestLayout class="bg-white">
        <HeroSection :image="GirlWithAss" title="You need to know" subtitle="About Us"
            description="Spicy Collab gives creators a safe, streamlined space to find and manage gigs, collaborate, and stay organized—all for free with identity verification."
            buttonText1="Join Spicycollab" buttonText2="Explore More" buttonLink1="/watilist" />
        <!-- Spacer -->
        <!-- <div class="h-44 md:h-72 lg:h-96 2xl:hidden"></div> -->

        <div
            class="flex flex-col max-w-6xl min-h-screen px-6 px-12 py-12 mx-auto overflow-hidden text-black md:flex-row">
            <!-- Main Title Section -->
            <div class="md:w-2/3">
                <header class="text-left">
                    <h1 class="text-4xl font-bold text-black font-title">About Us</h1>
                    <p class="max-w-4xl mx-auto mt-4 text-lg font-title">
                        Connecting Creators. Simplifying Collaboration. Securing Your Jobs.
                    </p>
                </header>

                <!-- Intro Paragraph -->
                <section class="max-w-4xl mx-auto mt-8">
                    <p class="text-lg leading-relaxed">
                        Welcome to
                        <span class="font-bold text-black">Spicy Collab</span>
                        — a platform built by a creator, for creators. Whether you're a
                        content creator, adult talent, photographer, videographer, producer,
                        studio, or any creative professional, Spicy Collab is designed to make
                        booking and collaborating easier, faster, and safer.
                    </p>
                    <p class="mt-4 text-lg leading-relaxed">
                        After decades in the entertainment and adult film industry as a
                        photographer, director, writer, and producer — booking talent for
                        studios, magazines, commercials, and advertising campaigns — I’ve
                        worked with thousands of creative professionals. Along the way, I
                        encountered the same challenges over and over:
                    </p>
                    <ul class="mt-4 ml-10 text-lg leading-relaxed list-disc list-inside">
                        <li>
                            Managing availability and knowing when talent was traveling.
                        </li>
                        <li>Keeping track of who I talked to, where, and when.</li>
                        <li>Storing and organizing job details and histories.</li>
                        <li>
                            Coordinating collaborations, ensuring everyone agreed on testing,
                            payment, and locations.
                        </li>
                    </ul>
                    <p class="mt-4 text-lg leading-relaxed">
                        Social media offered direct communication, but the disorganization
                        was frustrating. I knew there had to be a better way — and that’s
                        why I created Spicy Collab.
                    </p>
                </section>

                <!-- What Makes Spicy Collab Different Section -->
                <section class="mt-12">
                    <h2 class="text-4xl font-semibold text-black font-title">
                        What Makes Spicy Collab Different
                    </h2>
                    <ul class="mt-4 ml-6 space-y-3">
                        <li class="list-disc">
                            <span class="font-bold text-black">Easy-to-Use Calendar:</span>
                            <br />
                            <span>
                                Schedule your jobs, travel, and availability — and share it
                                effortlessly.
                            </span>
                        </li>
                        <li class="list-disc">
                            <span class="font-bold text-black">Powerful Search Engine:</span>
                            <br />
                            <span>
                                Find available talent or collaborators near you, even for
                                last-minute bookings.
                            </span>
                        </li>
                        <li class="list-disc">
                            <span class="font-bold text-black">Job Management:</span>
                            <br />
                            <span>Organize and store all your job details in one place.</span>
                        </li>
                        <li class="list-disc">
                            <span class="font-bold text-black">Seamless Chat System:</span>
                            <br />
                            <span>
                                Discuss job details, confirm offers, and streamline
                                communication.
                            </span>
                        </li>
                        <li class="list-disc">
                            <span class="font-bold text-black">Collaboration Tools:</span>
                            <br />
                            <span>
                                Plan and manage projects with new collaborators, ensuring
                                clarity on all key details.
                            </span>
                        </li>
                        <li class="list-disc">
                            <span class="font-bold text-black">Top-Tier Security:</span>
                            <br />
                            <span>
                                With two-factor authentication and ID verification powered by
                                Yoti, you know every connection is safe.
                            </span>
                        </li>
                    </ul>
                </section>

                <!-- Our Promise Section -->
                <section class="max-w-4xl mx-auto mt-12">
                    <h2 class="text-4xl font-semibold text-black font-title">
                        Our Promise
                    </h2>
                    <p class="mt-4 text-lg leading-relaxed">
                        We believe creative collaboration shouldn’t be complicated or
                        costly. That’s why
                        <span class="font-semibold">Spicy Collab</span>
                        offers a
                        <span class="font-semibold">low monthly fee</span>
                        — no per-job charges, no hidden costs.
                    </p>
                    <p class="mt-4 text-lg leading-relaxed">
                        Simplify your jobs. Secure your work. Collaborate with confidence.
                    </p>
                    <p class="mt-4 text-lg">
                        <Link href="/register" class="hover:underline">
                        Join Spicy Collab
                        </Link>
                        and take the stress out of booking and collaboration.
                    </p>
                </section>
            </div>
            <div class="px-4 md:w-1/3">
                <!-- Social Media -->
                <div class="px-2 py-6 mt-8 text-center border border-gray-300 rounded-lg">
                    <p>
                        Social media offered direct communication, but the disorganization
                        was frustrating. I knew there had to be a better way —
                    </p>
                    <div class="flex flex-col mt-2 text-xl font-semibold font-title">
                        <p>That's why I created</p>
                        <p class="text-primary">Spicy Collab</p>
                    </div>
                    <div class="flex justify-center mt-4 space-x-4">
                        <a href="https://www.instagram.com/spicycollab" target="_blank"
                            class="flex items-center justify-center w-8 h-8 bg-purple-500 rounded-full">
                            <i class="text-white fab fa-instagram"></i>
                        </a>
                        <a href="https://www.facebook.com" target="_blank"
                            class="flex items-center justify-center w-8 h-8 bg-purple-500 rounded-full" v-show="false">
                            <i class="text-white fab fa-facebook"></i>
                        </a>
                        <a href="https://x.com/spicycollab" target="_blank"
                            class="flex items-center justify-center w-8 h-8 bg-purple-500 rounded-full">
                            <i class="text-white fab fa-x-twitter"></i>
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <WelcomeConnectedSection class="-mt-4" />
        <WelcomeBlackDiv class="-mt-44" />
    </GuestLayout>
</template>

<style scoped></style>
